import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { eventNotesApi } from "../../api";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";

const AUTOSAVE_MS = 1200;

const EventNotesTab: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;

  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const timerRef = useRef<number | null>(null);

  const notesQuery = useQuery({
    queryKey: eventId ? queryKeys.eventNotes(workspace, eventId) : ["eventNotes", "idle"],
    queryFn: () => eventNotesApi.get(eventId!, token!),
    enabled: Boolean(eventId && token),
  });

  useEffect(() => {
    if (!eventId || notesQuery.data === undefined) return;
    setNotes(notesQuery.data.body);
    setDirty(false);
    setLastSavedAt(null);
  }, [eventId, notesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (body: string) => {
      if (!eventId) throw new Error("Missing event");
      return eventNotesApi.upsert(eventId, body, token!);
    },
    onSuccess: async () => {
      setDirty(false);
      setLastSavedAt(new Date());
      if (eventId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.eventNotes(workspace, eventId) });
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save notes"),
  });

  useEffect(() => {
    if (!eventId || !dirty) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      saveMutation.mutate(notes);
    }, AUTOSAVE_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, notes, dirty]);

  const saveNotes = () => {
    if (!eventId) return;
    saveMutation.mutate(notes);
    toast.success("Notes saved");
  };

  return (
    <div className="eventos-card">
      <div className="eventos-label">Notes</div>
      <p className="app-shell-lede" style={{ marginBottom: 12 }}>
        Freeform notes for this show.
      </p>

      {notesQuery.isError ? (
        <QueryErrorBanner
          message={notesQuery.error instanceof Error ? notesQuery.error.message : "Failed to load notes"}
          onRetry={() => void notesQuery.refetch()}
        />
      ) : null}

      <label className="eventos-field">
        <span className="eventos-field-label">Show notes</span>
        <textarea
          className="eventos-input"
          rows={8}
          value={notes}
          disabled={notesQuery.isPending}
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
          placeholder="Production notes, rider reminders, settlement notes…"
        />
      </label>
      <div className="app-form-actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="eventos-primary-btn"
          onClick={saveNotes}
          disabled={!dirty || !eventId || saveMutation.isPending}
        >
          Save now
        </button>
        {dirty || saveMutation.isPending ? <span className="eventos-subtle">Saving…</span> : null}
        {!dirty && !saveMutation.isPending && lastSavedAt ? (
          <span className="eventos-subtle">Saved {lastSavedAt.toLocaleTimeString()}</span>
        ) : null}
      </div>
    </div>
  );
};

export default EventNotesTab;
